import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators, FormControl } from "@angular/forms";
import { ActiveMeters, Meters, Reading, ActiveMetersFactory } from "../edp-consumptions.models";
import { RegisterTypeEnum } from "@edp-enums/register-type.enum";
import { Contract } from "@edp-components/edp-contract";
import { EdpModalService } from "@edp-components/edp-modal";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { EdpWidgetConsumptionsVoiceReadingComponent } from "@edp-components/edp-card-widgets/edp-widget-consumptions/edp-widget-consumptions-voice-reading/edp-widget-consumptions-voice-reading.component";
import { environment } from "@edp-environments/environment";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { take, takeUntil, distinctUntilChanged } from "rxjs/operators";
import { getDevice } from "@edp-store/selectors/notifications.selectors";
import { Subject } from "rxjs";
import { IDevice } from "@edp-models/device.model";
import { AppService } from "@edp-services/app.service";
import { RegisterDevice } from "@edp-store/actions/notifications.actions";
import { getUser } from "@edp-store/selectors/user.selectors";
import moment from "moment";
import { IntegerValidator } from "@edp-validators/integer.validator";
import { SpecialCharacterValidator } from "@edp-validators/special-character";
import { CheckLastInserted } from "@edp-validators/last-inserted.validator";

@Component({
    selector: "edp-widget-consumptions-electricity",
    templateUrl: "./edp-widget-consumptions-electricity.component.html",
    styleUrls: ["./edp-widget-consumptions-electricity.component.scss"],
})
export class EdpContractConsumptionsElectricityComponent implements OnInit, OnDestroy {
    public readingCode = {
        FV: "182",
        V: "181",
        P: "182",
        C: "183",
    };

    @Input() contract: Contract;
    @Input() set activeMeters(activeMeters: ActiveMeters) {
        if (activeMeters && activeMeters.meters.length) {
            this.consumptionsForm = this.formBuilder.group({
                typeS: new FormControl(null),
                typeFV: new FormControl(null),
                typeV: new FormControl(null),
                typeC: new FormControl(null),
                typeP: new FormControl(null),
            });

            this.activeMetersElectricity = ActiveMetersFactory.create({
                meters: [],
                lastInserted: activeMeters.lastInserted,
                lastValidReading: activeMeters.lastValidReading,
                simulation: activeMeters.simulation,
                supplyType: activeMeters.supplyType,
            } as ActiveMeters);

            activeMeters.meters.forEach(meter => {
                const meterData = { ...meter };
                meterData.OutsideConsumption = false;
                this.activeMetersElectricity.meters.push(meterData);
                this.isTwoReaders = this.activeMetersElectricity?.meters.length > 2;
                this.validateRegisterTypes(meter.RegisterType, 0, Number(meter.Predecimalplaces));
            });

            if (!!this.activeMetersElectricity.lastInserted) {
                this.setReadingsValidators();
            }
        }
    }
    @Output() submitted: EventEmitter<any> = new EventEmitter();
    @Output() focusEmit: EventEmitter<Meters> = new EventEmitter();
    @Output() blurEmit: EventEmitter<any> = new EventEmitter();

    public activeMetersElectricity: ActiveMeters;
    public showAlertMessage = false;
    public isSubmitted: boolean;

    public consumptionsForm: FormGroup;
    public RegisterTypeEnum = RegisterTypeEnum;
    public voiceAvailable = false;

    private unsubscribe = new Subject();
    private modalOptions: NgbModalOptions = {
        windowClass: "edp-dialog-fullscreen",
    };
    private modalData: IModalData = {
        title: "",
        showClose: true,
    };
    public noConsumptionData: boolean;
    public isTypeMeterError: boolean;
    public isTwoReaders: boolean;

    constructor(
        public formBuilder: FormBuilder,
        public edpModalService: EdpModalService,
        public store: Store<IAppState>,
        public appService: AppService
    ) {}

    ngOnInit() {
        if (environment.isApp) {
            this.store.pipe(takeUntil(this.unsubscribe), select(getDevice)).subscribe((device: IDevice) => {
                if (device) {
                    const osType = device.OsType.toLowerCase();
                    const appVersion = Number(device.AppVersion.replace(/[^0-9|]/g, ""));
                    if (environment.isApp && environment.production) {
                        // Voice available on versions:
                        // Android  6.0.28
                        // iOS      6.0.30
                        if ((osType === "android" && appVersion >= 6028) || (osType === "ios" && appVersion >= 6030)) {
                            this.voiceAvailable = true;
                        }
                    } else {
                        this.voiceAvailable = environment.isApp;
                    }
                } else {
                    this.appService.getDeviceId().subscribe((args: IDevice) => {
                        this.store.pipe(take(1), select(getUser)).subscribe(user => {
                            this.store.dispatch(new RegisterDevice({ ...args, FirebaseId: user.uid }));
                        });
                    });
                }
            });
        }
        if (!this.contract.Ird_start || !this.contract.Ird_end) {
            this.noConsumptionData = true;
        }

        this.consumptionsForm.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
            this.checkErrors();
        });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public onFocus(meter: Meters) {
        this.focusEmit.emit(meter);
    }

    public onBlur() {
        this.blurEmit.emit();
    }

    public onSubmit() {
        this.isSubmitted = true;
        const readings: Reading[] = [];
        const extUi = this.contract.BasicComponent.filter(
            component => component.SupplyType === this.activeMetersElectricity.supplyType
        ).map(x => x.DeliveryPoint)[0];

        this.activeMetersElectricity.meters.forEach(meter => {
            readings.push(
                new Reading(
                    meter.Brand,
                    meter.Material,
                    meter.Model,
                    meter.Serialno,
                    meter.Register,
                    meter.RegisterType,
                    meter.RegisterTypeStr,
                    meter.ReadingResult,
                    extUi
                )
            );
        });

        const args: any = {
            contractId: this.contract.ContractId,
            SupplyType: this.activeMetersElectricity.supplyType,
            ReadingsList: readings,
        };

        this.submitted.emit(args);
    }

    public consumptionsFormChange(event: any, meter: Meters) {
        meter.ReadingResult = event.target.value;
        meter.OutsideConsumption = false;

        if (meter.ReadingResult.length) {
            if (Number(meter.ReadingResult) > Number(meter.MaxRead) || Number(meter.ReadingResult) < Number(meter.MinRead)) {
                meter.OutsideConsumption = true;
            }
        }
        this.showErrorMessage();
    }

    private showErrorMessage() {
        const totalOutsideConsumption = this.activeMetersElectricity.meters.filter(
            meter => meter.OutsideConsumption && meter.ReadingResult.length
        ).length;

        if (totalOutsideConsumption === 0) {
            this.showAlertMessage = false;
        } else {
            this.showAlertMessage = true;
        }
    }

    public getMaxReadValue = (type: string): string | null => {
        const getRegisterType = item => item.RegisterType === type;
        const maxRead = this.activeMetersElectricity.meters.filter(getRegisterType)[0].MaxRead;
        return maxRead;
    }

    public validateRegisterTypes(meter, min, maxLength) {
        const defaultValidators = [
            Validators.required,
            Validators.min(min),
            Validators.maxLength(!!maxLength ? maxLength : 8),
            IntegerValidator,
            SpecialCharacterValidator,
        ];

        switch (meter) {
            case RegisterTypeEnum[RegisterTypeEnum.S]:
                this.consumptionsForm
                    .get("typeS")
                    .setValidators([
                        ...defaultValidators,
                        CheckLastInserted(this.activeMetersElectricity.lastInserted.S, this.getMaxReadValue("S")),
                    ]);
                break;
            case RegisterTypeEnum[RegisterTypeEnum.FV]:
                this.consumptionsForm
                    .get("typeFV")
                    .setValidators([
                        ...defaultValidators,
                        CheckLastInserted(this.activeMetersElectricity.lastInserted.FV, this.getMaxReadValue("FV")),
                    ]);
                break;
            case RegisterTypeEnum[RegisterTypeEnum.V]:
                this.consumptionsForm
                    .get("typeV")
                    .setValidators([
                        ...defaultValidators,
                        CheckLastInserted(this.activeMetersElectricity.lastInserted.V, this.getMaxReadValue("V")),
                    ]);
                break;
            case RegisterTypeEnum[RegisterTypeEnum.C]:
                this.consumptionsForm
                    .get("typeC")
                    .setValidators([
                        ...defaultValidators,
                        CheckLastInserted(this.activeMetersElectricity.lastInserted.C, this.getMaxReadValue("C")),
                    ]);
                break;
            case RegisterTypeEnum[RegisterTypeEnum.P]:
                this.consumptionsForm
                    .get("typeP")
                    .setValidators([
                        ...defaultValidators,
                        CheckLastInserted(this.activeMetersElectricity.lastInserted.P, this.getMaxReadValue("P")),
                    ]);
                break;
        }

        this.consumptionsForm.updateValueAndValidity();
    }

    public checkErrors() {
        this.isTypeMeterError =
            (this.consumptionsForm.get("typeS").invalid && !this.consumptionsForm.get("typeS").errors?.required) ||
            (this.consumptionsForm.get("typeFV").invalid && !this.consumptionsForm.get("typeFV").errors?.required) ||
            (this.consumptionsForm.get("typeV").invalid && !this.consumptionsForm.get("typeV").errors?.required) ||
            (this.consumptionsForm.get("typeP").invalid && !this.consumptionsForm.get("typeP").errors?.required) ||
            (this.consumptionsForm.get("typeC").invalid && !this.consumptionsForm.get("typeC").errors?.required);
    }

    public showVoiceModal() {
        this.edpModalService
            .custom(EdpWidgetConsumptionsVoiceReadingComponent, this.modalOptions, this.modalData)
            .then(readingResultArgs => {
                if (readingResultArgs) {
                    this.submitted.emit(readingResultArgs);
                }
            })
            .catch(() => {
                /* Intentional silent catch */
            });
    }

    public setReadingsValidators() {
        const actualDate = moment().format("YYYY-MM-DD");
        const lastInserted = this.activeMetersElectricity.lastInserted;
        const lastValidReading = this.activeMetersElectricity.lastValidReading;

        this.activeMetersElectricity.meters.forEach(el => {
            let min = moment(actualDate).isSame(lastInserted.date)
                ? lastValidReading
                    ? lastValidReading[el.RegisterType]
                    : 0
                : lastInserted[el.RegisterType];

            if (Number(el.MaxRead) < lastInserted[el.RegisterType] || el.MaxRead === null) {
                min = 0;
            }

            this.validateRegisterTypes(el.RegisterType, !!min ? min : 0, Number(el.Predecimalplaces));
        });
    }
}
